import React, {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {Link, useLocation} from "react-router-dom";
import {predictValidationAction} from "../../redux/toolkit/predictValidationSlice";
import DashboardContainer from "../sideBar/DashboardContainer";
import Message from "../../helper/messages/Message";
import Loading from "../../helper/Loading";
import PageLayout, {Card, PREDICT_STEPS} from "../layout/PageLayout";
import c from "./predictValidation.module.css";

const PredictValidationContainer = () => {
    let location = useLocation();
    const dispatch = useDispatch();
    const predictValidationData = useSelector(state => state?.predictValidationReducer);
    const {result, status_code, status, message} = predictValidationData?.predictValidationResult;
    const {loading} = predictValidationData;

    const platform_request_id = localStorage.getItem('platform_request_id')
    // Read once, and checked before the button is offered. It used to be read
    // inline into `to`, and nothing has ever written it - so an eLabNext user got a
    // button whose destination was null. Falling back to "Show performed tests" is
    // the behaviour a non-platform user already gets, and it goes somewhere.
    const elabnextLink = localStorage.getItem('elabnext_link')

    useEffect(() => {
        if (location.state) {
            dispatch(predictValidationAction(location.state));
        }
        return () => {
            // localStorage.removeItem('platform_request_id');
            // localStorage.removeItem('requestToken');
            // localStorage.removeItem('elabnext_link');
        }
    }, [dispatch, location.state]);

    // A refused endpoint comes back with a message but no request_id, so split
    // the two rather than rendering blank cards for the refusals.
    const submitted = useMemo(() => (result || []).filter(i => i["request_id"]), [result]);
    const skipped   = useMemo(() => (result || []).filter(i => !i["request_id"]), [result]);

    return (
        <DashboardContainer>
            <PageLayout title='Predict'
                        subtitle='Your compounds are queued. Results appear under Performed Tests once each run completes.'
                        steps={PREDICT_STEPS} currentStep={3} narrow>
                {
                    (!loading && status === 'success' && result?.length > 0) &&
                    <>
                        <Card>
                            <p className={c.summaryLine}>
                                <span className={c.summaryNumber}>{submitted.length}</span>
                                {' '}endpoint{submitted.length === 1 ? '' : 's'} submitted
                                {skipped.length > 0 &&
                                    <span className={c.summaryScope}> · {skipped.length} skipped</span>}
                            </p>
                            {skipped.length > 0 && (
                                <p className={c.skippedNote}>
                                    No active subscription for{' '}
                                    {skipped.map(i => i["assay_name"]).filter(Boolean).join(', ')
                                        || `${skipped.length} of the selected endpoints`}.
                                </p>
                            )}
                        </Card>

                        {/* One row per endpoint. A card each ran to thousands of pixels
                            once more than a handful of endpoints were selected. */}
                        {submitted.length > 0 && (
                            <Card padded={false}>
                                <div className={c.tableWrap}>
                                    <table className={c.resultTable}>
                                        <thead>
                                            <tr>
                                                <th>Endpoint</th>
                                                <th>Group</th>
                                                <th>Request ID</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {submitted.map((i, k) => (
                                                <tr key={k}>
                                                    <td>{i["assay_name"]}</td>
                                                    <td className={c.muted}>{i["group_name"]}</td>
                                                    {/* an opaque UUID is reference material, not
                                                        headline content - muted, select-all to copy */}
                                                    <td className={c.requestId}>{i["request_id"]}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        )}

                        {/* The table scrolls within a fixed height, so this stays on
                            screen however many endpoints were submitted. */}
                        {/* rel="noreferrer" on the eLabNext link below, like every
                            other blank-target link here. eslint's jsx-no-target-blank
                            checks <a> and not <Link>, which renders one - and that `to`
                            comes from localStorage, written by the eLabNext platform
                            rather than by us. */}
                        <div className={c.predictActions}>
                            {(!platform_request_id || !elabnextLink)
                                ? <Link className={c.primaryLink} to='/performed'>Show performed tests</Link>
                                : <Link className={c.primaryLink} target={'_blank'} rel="noreferrer"
                                        to={elabnextLink}>
                                    Open in eLabNext
                                </Link>}
                        </div>
                    </>
                }
                {
                    status === 'error' && status_code !== 200 && !loading &&
                    <Message message={message} status={status} status_code={status_code}/>
                }
                {
                    loading && <Loading/>
                }
            </PageLayout>
        </DashboardContainer>
    );
};

export default PredictValidationContainer;
